import localForage from "localforage";
import CryptoJS from "crypto-js";
import { ILogEvent, Logo, IPersonBadge, Nullable, BadgeImagesBySku } from "../types";
import { FormValues as INewBadge } from "../../components/createBadge/utils/types";

const badgesKey = "bdgs";
const badgeEventsKey = "bd-eve";
const logosKey = "logos";
const badgeImagesKey = "bi";
const newRegsKey = "n-bd";
const eventLogoKey = "el";

const getBadgesKey = () => badgesKey;
const getBadgeEventsKey = () => badgeEventsKey;
const getNewRegsKey = () => newRegsKey;

const addLogEvent = async (event: ILogEvent) => {
  const eventsFromStorage: ILogEvent[] | null = await getLogEvents();
  eventsFromStorage.push(event);
  await localForage.setItem(badgeEventsKey, eventsFromStorage);
};

const getLogEvents = async () => {
  const eventFromStorage: ILogEvent[] | null = await localForage.getItem(badgeEventsKey);
  if (!eventFromStorage) {
    return [];
  }
  return eventFromStorage;
};

const getDecryptedItem = async (key: string, passphrase: string) => {
  try {
    const value = await localForage.getItem(key);
    let decrypted: null | string = null;
    if (value) {
      decrypted = CryptoJS.AES.decrypt(value as string, passphrase).toString(CryptoJS.enc.Utf8);
    }
    return decrypted ? JSON.parse(decrypted) : null;
  } catch (e) {}
};

const getNewRegistrations = async (passphrase: string) => {
  return getDecryptedItem(getNewRegsKey(), passphrase);
};

const setEncryptedItem = async (key: string, value: any, passphrase: string) => {
  const encrypted = CryptoJS.AES.encrypt(JSON.stringify(value), passphrase).toString();
  await localForage.setItem(key, encrypted);
};

const deleteItem = key => {
  return localForage.removeItem(key);
};

const pushItemToArray = async (key: string, passphrase: string, obj: INewBadge) => {
  let fromStore = await getDecryptedItem(key, passphrase);
  if (!fromStore || !Array.isArray(fromStore)) {
    fromStore = [];
  }
  fromStore.push(obj);
  await setEncryptedItem(key, fromStore, passphrase);
};

const removeItemFromArray = async (key: string, passphrase: string, id: string | number) => {
  const fromStore = await getDecryptedItem(key, passphrase);

  if (!fromStore || !Array.isArray(fromStore)) {
    return;
  }
  const updatedArray = fromStore.filter(b => b.id !== id);

  if (updatedArray.length === 0) {
    await deleteItem(key);
    return;
  }
  await setEncryptedItem(key, updatedArray, passphrase);
};

const addNewRegistrationToStorage = async (registration: INewBadge, passphrase: string) => {
  await pushItemToArray(newRegsKey, passphrase, registration);
};

const removeNewRegFromStorage = async (id: string | number, passphrase: string) => {
  await removeItemFromArray(newRegsKey, passphrase, id);
};

const addBadgeToStorage = async (badge: IPersonBadge, passphrase: string) => {
  let badgesFromStore = await getDecryptedItem(badgesKey, passphrase);
  if (!badgesFromStore || !Array.isArray(badgesFromStore)) {
    badgesFromStore = [];
  }
  badgesFromStore.push(badge);
  await setEncryptedItem(badgesKey, badgesFromStore, passphrase);
};

const removeBadgesFromStorage = async (idsToDelete: string[], passphrase: string) => {
  const badgesFromStore = await getDecryptedItem(badgesKey, passphrase);

  if (!badgesFromStore || !Array.isArray(badgesFromStore)) {
    return;
  }
  const updatedArray = (badgesFromStore as Array<IPersonBadge>).filter(b => Boolean(!idsToDelete.find(id => id === b.id)));
  if (updatedArray.length === 0) {
    await deleteItem(badgesKey);
    return;
  }
  await setEncryptedItem(badgesKey, updatedArray, passphrase);
};

const getLogosFromStorage = async () => {
  const logosFromStorage: Nullable<Logo[]> = await localForage.getItem(logosKey);
  if (!logosFromStorage) {
    return [];
  }
  return logosFromStorage;
};

const getLogoFromStorageBySku = async (sku: string) => {
  const logos = await getLogosFromStorage();

  return logos.find(logo => logo.sku === sku);
};

const addLogosToStorage = async (logos: Logo[]) => {
  await localForage.setItem(logosKey, logos);
};

const clearUpLogosFromStorage = async () => {
  await localForage.setItem(logosKey, []);
};

const getBadgeImagesFromStorage = async () => {
  const imagesFromStorage: Nullable<BadgeImagesBySku[]> = await localForage.getItem(badgeImagesKey);
  if (!imagesFromStorage) {
    return [];
  }
  return imagesFromStorage;
};

const getBadgeImagesForSku = async (sku: string) => {
  const images = await getBadgeImagesFromStorage();

  if (!images) return;

  return images.find(image => image.sku === sku);
};

const addBadgeImagesToStorage = async (images: BadgeImagesBySku[]) => {
  await localForage.setItem(badgeImagesKey, images);
};

const clearUpBadgeImagesFromStorage = async () => {
  await localForage.setItem(badgeImagesKey, []);
};

const addEventLogoToStorage = async (logo: string) => {
  await localForage.setItem(eventLogoKey, logo);
};

const getEventLogoFromStorage = async (): Promise<string> => {
  const logo = (await localForage.getItem(eventLogoKey)) as Nullable<string>;
  return logo || "";
};

const getOutOfSyncBadgesCount = async () => {
  return OfflineStorage.getLogEvents().then(badges => badges.length);
};

const OfflineStorage = {
  addEventLogoToStorage,
  getEventLogoFromStorage,
  addNewRegistrationToStorage,
  removeNewRegFromStorage,
  getDecryptedItem,
  setEncryptedItem,
  deleteItem,
  addBadgeToStorage,
  removeBadgesFromStorage,
  getBadgesKey,
  addLogEvent,
  getLogEvents,
  getBadgeEventsKey,
  getNewRegsKey,
  getNewRegistrations,
  getLogosFromStorage,
  addLogosToStorage,
  getLogoFromStorageBySku,
  clearUpLogosFromStorage,
  getBadgeImagesFromStorage,
  getBadgeImagesForSku,
  addBadgeImagesToStorage,
  clearUpBadgeImagesFromStorage,
  getOutOfSyncBadgesCount
};

export default OfflineStorage;
